import { useNavigate } from 'react-router-dom';

function NCODashboard({ data }) {
  const navigate = useNavigate();
  const teams = data?.team_overview || [];
  const attendance = data?.attendance || {};
  const incidents = data?.recent_incidents || [];

  return (
    <div className="space-y-6" data-testid="nco-dashboard">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">NCO Dashboard</h1>
        <p className="text-gray-400">Team management and operations</p>
      </div>

      {/* Team Overview */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h2 className="text-xl font-bold text-white mb-4">My Teams</h2>
        {teams.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {teams.map(team => (
              <TeamCard key={team.team_id} team={team} />
            ))}
          </div>
        ) : (
          <p className="text-gray-400 text-center py-8">No teams assigned. Contact your commanding officer.</p>
        )}
      </div>

      {/* Attendance */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h2 className="text-xl font-bold text-white mb-4">Today&apos;s Attendance</h2>
        <div className="grid grid-cols-3 gap-4">
          <StatCard value={attendance.total_expected || 0} label="Expected" color="white" />
          <StatCard value={attendance.checked_in || 0} label="Checked In" color="green" />
          <StatCard value={attendance.checked_out || 0} label="Checked Out" color="blue" />
        </div>
      </div>

      {/* Incidents */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h2 className="text-xl font-bold text-white mb-4">Recent Incidents</h2>
        {incidents.length > 0 ? (
          <div className="space-y-2">
            {incidents.slice(0, 5).map(incident => (
              <IncidentRow key={incident.id} incident={incident} />
            ))}
          </div>
        ) : (
          <p className="text-gray-400 text-center py-4">No recent incidents</p>
        )}
        <button onClick={() => navigate('/deployments')} className="mt-4 w-full px-4 py-2 bg-red-600 hover:bg-red-500 rounded-lg text-white font-medium">
          Report New Incident
        </button>
      </div>
    </div>
  );
}

function TeamCard({ team }) {
  const riskColors = {
    Critical: 'bg-red-500/20 text-red-400',
    High: 'bg-orange-500/20 text-orange-400',
    Medium: 'bg-yellow-500/20 text-yellow-400',
    Low: 'bg-green-500/20 text-green-400'
  };
  const riskClass = riskColors[team.site_risk] || riskColors.Low;

  return (
    <div className="bg-gray-700 rounded-lg p-4">
      <div className="flex justify-between items-start mb-3">
        <div>
          <p className="text-white font-medium">{team.team_name}</p>
          <p className="text-gray-400 text-sm">{team.site_name}</p>
        </div>
        <span className={`px-2 py-1 rounded text-xs ${riskClass}`}>{team.site_risk || 'Low'} Risk</span>
      </div>
      <p className="text-gray-300 text-sm">{team.member_count} members assigned</p>
      {team.members && team.members.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {team.members.slice(0, 4).map(m => (
            <span key={m.service_number} className="px-2 py-1 bg-gray-600 rounded text-xs text-gray-300">
              {m.full_name?.split(' ')[0]}
            </span>
          ))}
          {team.members.length > 4 && (
            <span className="px-2 py-1 bg-gray-600 rounded text-xs text-gray-400">+{team.members.length - 4}</span>
          )}
        </div>
      )}
    </div>
  );
}

function StatCard({ value, label, color }) {
  const colorClasses = {
    white: 'text-white',
    green: 'text-green-400',
    blue: 'text-blue-400',
    red: 'text-red-400',
    amber: 'text-amber-400'
  };
  return (
    <div className="bg-gray-700 rounded-lg p-4 text-center">
      <p className={`text-3xl font-bold ${colorClasses[color]}`}>{value}</p>
      <p className="text-gray-400 text-sm">{label}</p>
    </div>
  );
}

function IncidentRow({ incident }) {
  const statusColors = {
    open: 'bg-blue-500/20 text-blue-400',
    investigating: 'bg-yellow-500/20 text-yellow-400',
    closed: 'bg-green-500/20 text-green-400'
  };
  const statusClass = statusColors[incident.status] || statusColors.open;

  return (
    <div className="bg-gray-700 rounded-lg p-3 flex justify-between items-center">
      <div>
        <p className="text-white font-medium">{incident.incident_number}</p>
        <p className="text-gray-400 text-sm">{incident.type}</p>
      </div>
      <span className={`px-2 py-1 rounded text-xs ${statusClass}`}>{incident.status}</span>
    </div>
  );
}

export default NCODashboard;
